import { Component, OnInit, OnDestroy, inject, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { GenericFormlyFormComponent } from '@intalio/formly-builder/bootstrap';
import {
  ServicesFormService,
  FormSchemaResponse,
  User,
} from '../../shared/services/services-form.service';
import { RequestManagementService } from '../../shared/services/request-management.service';
import { NotificationService } from '../../shared/services/notification.service';
import { Request } from '../../shared/models/request-models';

@Component({
  selector: 'app-dynamic-form',
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.scss'],
  standalone: true,
  imports: [TranslateModule, CommonModule, GenericFormlyFormComponent],
})
export class DynamicFormComponent implements OnInit, OnDestroy {
  @ViewChild(GenericFormlyFormComponent) genericForm?: GenericFormlyFormComponent;

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private formService = inject(ServicesFormService);
  private requestService = inject(RequestManagementService);
  private notificationService = inject(NotificationService);
  private translate = inject(TranslateService);

  /** Raw form definition JSON from the API — passed directly to the library */
  formDefinition: any = null;
  model: any = {};
  requestTypeLogicalId: string = '';
  requestId: number | null = null;

  currentUser: User | null = null;
  isLoading: boolean = true;
  isSubmitting: boolean = false;
  isSavingDraft: boolean = false;
  isUpdatingDraft: boolean = false;
  isUpdatingAndSubmitting: boolean = false;
  apiErrors: string[] = [];
  successMessage: string = '';
  currentLanguage: string = 'en';

  // Draft mode properties
  draftId: string | null = null;
  isDraftMode: boolean = false;

  private documentTypeId: string | null = null;
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.currentLanguage = this.translate.currentLang || this.translate.defaultLang || 'en';
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      this.currentLanguage = event.lang;
    });

    this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.documentTypeId = params['documentTypeId'];
    });

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.draftId = params['draftId'] || null;
      this.isDraftMode = !!this.draftId;
    });

    this.notificationService
      .ensureUserInitialized()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (userId) => {
          this.formService
            .getUser(+userId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (user) => {
                this.currentUser = user;
                this.handleAfterUserInitialized();
              },
              error: (err) => {
                console.error('Failed to load user:', err);
                this.apiErrors = ['Failed to load user information. Please try again.'];
                this.isLoading = false;
              },
            });
        },
        error: (err) => {
          console.error('Failed to initialize user:', err);
          this.apiErrors = ['Failed to initialize user. Please try again.'];
          this.isLoading = false;
        },
      });
  }

  private handleAfterUserInitialized(): void {
    if (this.isDraftMode && this.draftId) {
      this.loadDraftForEditing();
    } else if (this.documentTypeId) {
      this.loadFormSchema();
    } else {
      this.apiErrors = ['Invalid service ID'];
      this.isLoading = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadDraftForEditing(): void {
    if (!this.draftId) {
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    this.apiErrors = [];

    this.requestService
      .getRequestById(this.draftId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (request: Request) => {
          if (request.summary.status !== 'Draft') {
            this.apiErrors = ['This request is no longer a draft'];
            this.isLoading = false;
            return;
          }

          try {
            this.model = JSON.parse(request.details.formData);
          } catch (e) {
            console.error('[DynamicForm] Error parsing draft form data:', e);
            this.apiErrors = ['Error parsing draft form data'];
            this.isLoading = false;
            return;
          }

          this.requestTypeLogicalId = request.summary.typeLogicalId;
          this.documentTypeId = request.summary.typeLogicalId;
          this.requestId = Number(this.draftId) || null;
          this.loadFormSchema();
        },
        error: (error) => {
          console.error('[DynamicForm] Error loading draft request:', error);
          this.apiErrors = ['Error loading draft request. Please try again.'];
          this.isLoading = false;
        },
      });
  }

  private loadFormSchema(): void {
    if (!this.documentTypeId) {
      this.apiErrors = ['Invalid document type ID'];
      this.isLoading = false;
      return;
    }

    this.formService
      .getFormSchema(this.documentTypeId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: FormSchemaResponse) => {
          this.requestTypeLogicalId = response.requestTypeLogicalId;
          // Pass raw formJson — the library's DynamicFormService processes it internally
          this.formDefinition = response.formJson;
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Failed to load form schema:', err);
          this.apiErrors = ['Failed to load form. Please try again.'];
          this.isLoading = false;
        },
      });
  }

  updateDraft(): void {
    this.genericForm?.onSubmit('saveasdraft');
  }

  updateDraftAndSubmit(): void {
    this.genericForm?.onSubmit('save');
  }

  submitForm(): void {
    this.genericForm?.onSubmit('submit');
  }

  saveDraft(): void {
    this.genericForm?.onSubmit('saveasdraft');
  }

  onFormSubmit(event: {
    action: string;
    formData: FormData;
    files: File[];
    fileMetadata: any[];
    requestTypeLogicalId: string;
    requestId: number | null;
    originalModel: any;
  }): void {
    const action = event.action.toLowerCase();

    // Append app-specific context fields not known by the library
    event.formData.append('department', this.currentUser?.structureId?.toString() || '');
    event.formData.append('requestTypeLogicalId', this.requestTypeLogicalId);
    event.formData.append('year', new Date().getFullYear().toString());
    event.formData.append('metadata', '');
    event.formData.append('parentRequest', '');
    event.formData.append('regardingId', '');
    event.formData.append('regardingType', '');

    if (this.isDraftMode) {
      if (action === 'saveasdraft') {
        this.isUpdatingDraft = true;
        this.apiErrors = [];
        this.successMessage = '';
        event.formData.append('requestId', this.draftId || '');

        this.formService
          .updateDraft(this.draftId || '', event.formData)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.isUpdatingDraft = false;
              this.successMessage = this.translate.instant('requests.draftUpdated');
              setTimeout(() => this.router.navigate(['/requests/drafts']), 2000);
            },
            error: (error) => {
              this.isUpdatingDraft = false;
              this.apiErrors = [error?.error?.message || 'Failed to update draft. Please try again.'];
            },
          });
      } else if (action === 'save' || action === 'submit') {
        this.isUpdatingAndSubmitting = true;
        this.apiErrors = [];
        this.successMessage = '';
        event.formData.append('requestId', this.draftId || '');

        this.formService
          .updateAndSubmitDraft(event.formData)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.isUpdatingAndSubmitting = false;
              this.successMessage = this.translate.instant('requests.draftSubmitted');
              setTimeout(() => this.router.navigate(['/requests/my-sent']), 2000);
            },
            error: (error) => {
              this.isUpdatingAndSubmitting = false;
              this.apiErrors = [error?.error?.message || 'Failed to submit draft. Please try again.'];
            },
          });
      }
    } else {
      if (action === 'saveasdraft') {
        this.isSavingDraft = true;
        this.apiErrors = [];
        this.successMessage = '';

        this.formService
          .saveDraft(event.formData)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.isSavingDraft = false;
              this.successMessage = this.translate.instant('requests.draftSaved');
              setTimeout(() => this.router.navigate(['/requests/drafts']), 2000);
            },
            error: (err) => {
              this.isSavingDraft = false;
              this.apiErrors = [err.error?.message || 'Failed to save draft. Please try again.'];
            },
          });
      } else if (action === 'submit') {
        this.isSubmitting = true;
        this.apiErrors = [];
        this.successMessage = '';

        this.formService
          .submitForm(event.formData)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.isSubmitting = false;
              this.successMessage = 'Request submitted successfully.';
              setTimeout(() => this.router.navigate(['/requests/my-sent']), 2000);
            },
            error: (err) => {
              this.isSubmitting = false;
              this.apiErrors = [err.error?.message || 'Failed to submit form. Please try again.'];
            },
          });
      }
    }
  }

  cancel(): void {
    this.router.navigate(['/dashboard']);
  }

  getDir(): string {
    return this.currentLanguage === 'ar' ? 'rtl' : 'ltr';
  }
}
